import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { FC, useMemo } from 'react'
import { styled } from '../../../lib/stitches'
import { CloseIcon } from '../../core/icons/icons'
import { gray1, gray15, gray2, gray3, gray4, gray5, gray8 } from '../../platform/color'

const ChevronDown = (props) => (
  <svg
    {...props}
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    ></path>
  </svg>
)

const CheckIcon = (props) => (
  <svg
    {...props}
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    ></path>
  </svg>
)

const StyledCheckIcon = styled(CheckIcon, {
  color: '#4992E8'
})

const StyledClose = styled(CloseIcon, {
  marginLeft: '8px'
})

const StyledTagContainer = styled('span', {
  whiteSpace: 'nowrap',
  marginRight: '4px',
  borderRadius: '9999px',
  height: '22px',
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '8px',
  paddingRight: '5px',
  background: gray15,
  color: gray1,
  fontSize: '12px',
  lineHeight: '22px'
})

const Tag = ({ title, onClick }: { title: string; onClick?: (evt) => void }) => (
  <StyledTagContainer>
    {title}
    {/* TODO make this work */}
    {/* <StyledClose onClick={onClick} /> */}
  </StyledTagContainer>
)

const StyledDropdownPreview = styled('div', {
  position: 'relative',
  height: '42px',
  boxSizing: 'border-box',
  padding: '10px 12px',
  border: '1px solid ' + gray5,
  borderRadius: '6px',
  background: 'transparent',
  display: 'flex',
  flexWrap: 'nowrap',
  overflow: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  '-ms-overflow-style': 'none',
  scrollbarWidth: 'none',
  alignItems: 'center',
  outline: 'none',
  color: gray15,
  transition: 'all 100ms ease',
  marginBottom: '4px',
  '&:focus': {
    boxShadow: '0px 0px 0px 3px ' + gray3
  }
})

const StyledDropdownPlaceholder = styled('span', {
  fontSize: '14px',
  lineHeight: '20px',
  color: gray8
})
const StyledChevronIcon = styled(ChevronDown, {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)'
})

const StyledDropdownContent = styled(DropdownMenuPrimitive.Content, {
  zIndex: 5000,
  transform: 'translateY(4px)',
  width: '360px',
  maxHeight: '300px',
  borderRadius: '6px',
  border: '1px solid ' + gray5,
  boxShadow: '0px 12px 16px -4px rgba(0, 0, 0, 0.15)',
  background: gray2,
  overflowX: 'hidden',
  overflowY: 'scroll',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  '-ms-overflow-style': 'none',
  scrollbarWidth: 'none'
})

const StyledCheckboxItem = styled(DropdownMenuPrimitive.CheckboxItem, {
  outline: 'none',
  cursor: 'pointer',
  position: 'relative',
  fontSize: '14px',
  lineHeight: '20px',
  paddingLeft: '48px',
  paddingTop: '6px',
  paddingBottom: '6px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  '&:hover': {
    background: gray3
  },
  '&:active': {
    background: gray4
  },
  '&:focus': {
    background: gray3
  }
})

const StyledCheckbox = styled('span', {
  position: 'absolute',
  left: '18px',
  borderRadius: '3px',
  color: gray8,
  border: '1px solid currentColor',
  width: '16px',
  height: '16px',
  boxSizing: 'border-box',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  variants: {
    active: {
      true: {
        color: '#4992E8'
      }
    }
  }
})

const DropdownMenu = DropdownMenuPrimitive.Root
const DropdownTrigger = DropdownMenuPrimitive.Trigger
const DropdownPreview = StyledDropdownPreview
const DropdownPlaceholder = StyledDropdownPlaceholder
const DropdownChevronIcon = StyledChevronIcon
const DropdownContent = StyledDropdownContent
const DropdownCheckboxItem = StyledCheckboxItem
const DropdownItemIndicator = DropdownMenuPrimitive.ItemIndicator
const DropdownCheckbox = StyledCheckbox
const DropdownCheckIcon = StyledCheckIcon
const DropdownPortal = DropdownMenuPrimitive.Portal

interface MultiSelectProps {
  onSelect?: (value: string) => void
  onRemove?: (value: string) => void
  options: readonly string[]
  values: Set<string>
  isDisabled?: boolean
}
const Multiselect: FC<MultiSelectProps> = ({ values, options, onSelect, onRemove, isDisabled }) => {
  const empty = values.size === 0
  const iterableValues = useMemo(() => Array.from(values), [values])

  return (
    <DropdownMenu dir="ltr">
      <DropdownTrigger asChild disabled={isDisabled}>
        <DropdownPreview>
          {empty && <DropdownPlaceholder>Choose genres</DropdownPlaceholder>}
          {iterableValues.map((e, i) => (
            <Tag
              title={e}
              key={i}
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                onSelect(e)
              }}
            />
          ))}
          <DropdownChevronIcon />
        </DropdownPreview>
      </DropdownTrigger>
      <DropdownPortal>
        <DropdownContent>
          {options.map((e, i) => {
            const isChecked = values.has(e)
            return (
              <DropdownCheckboxItem
                key={i}
                checked={isChecked}
                onSelect={(evt) => {
                  evt.preventDefault()
                  isChecked ? onRemove(e) : onSelect(e)
                }}
              >
                <DropdownCheckbox active={isChecked}>
                  <DropdownItemIndicator>
                    <DropdownCheckIcon />
                  </DropdownItemIndicator>
                </DropdownCheckbox>
                {e}
              </DropdownCheckboxItem>
            )
          })}
        </DropdownContent>
      </DropdownPortal>
    </DropdownMenu>
  )
}

export default Multiselect
